import * as layout from '../../../components/markLayout.module.css';
import Section from '@hero/ui-kit/layout/Section';
import MarkModal from '../../../components/MarkModal';
import { MarkButton, MarkHeading } from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import useCancelBlast from '../../../api/useCancelBlast';
import useBlastStats from '../../../api/useBlastStats';
import { extractBlastErrorMessage } from '../blastFormUtils';
import type { BlastDetail } from '../../../types';
type Props = {
    blast: BlastDetail;
    onClose: () => void;
    onSuccess: () => void;
};
const CancelModal: React.FC<Props> = ({ blast, onClose, onSuccess }) => {
    const cancel = useCancelBlast();
    const {
        data: statsResponse,
        isLoading: statsLoading,
        isError: statsError
    } = useBlastStats(blast.blast_id, blast.status);
    const headingId = useId();
    const isSending = blast.status === 'SENDING';
    const handleConfirm = () => {
        cancel.mutate({ blast_id: blast.blast_id }, { onSuccess: () => onSuccess() });
    };
    const sentCount = statsResponse?.data?.totals?.sent ?? null;
    const remaining =
        sentCount != null && blast.recipient_count != null
            ? blast.recipient_count - sentCount
            : null;
    return (
        <MarkModal
            onClose={onClose}
            isPending={cancel.isPending}
            aria-labelledby={headingId}
            styles={{
                width: 'min(64rem, 90vw)'
            }}
        >
            <Section noDefaultPadding className={layout.form}>
                <MarkHeading
                    role="h4"
                    id={headingId}
                    tabIndex={-1}
                    style={{
                        margin: '0 0 1rem'
                    }}
                >
                    Cancel blast
                </MarkHeading>
                {isSending ? (
                    <>
                        <P
                            noDefaultMargin
                            styles={{
                                marginBottom: '0.8rem'
                            }}
                        >
                            Unsent recipients will be marked <strong>cancelled</strong>.
                            Already-sent messages cannot be pulled back.
                        </P>
                        {statsLoading ? (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Loading send progress…
                            </P>
                        ) : statsError ? (
                            <P
                                role="alert"
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-danger)'
                                }}
                            >
                                Could not load send progress.
                            </P>
                        ) : sentCount != null ? (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Already sent: ~{sentCount.toLocaleString()}.
                                {remaining != null && (
                                    <> Remaining: ~{remaining.toLocaleString()}.</>
                                )}
                            </P>
                        ) : (
                            <P
                                noDefaultMargin
                                size="small"
                                styles={{
                                    color: 'var(--mark-muted)'
                                }}
                            >
                                Sent count unavailable.
                            </P>
                        )}
                    </>
                ) : (
                    <P
                        noDefaultMargin
                        styles={{
                            marginBottom: '0.8rem'
                        }}
                    >
                        This will cancel the scheduled send. No mail will go out.
                    </P>
                )}
                {cancel.error && (
                    <div
                        role="alert"
                        style={{
                            marginTop: '0.8rem'
                        }}
                        className={layout.notice}
                    >
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-danger)'
                            }}
                        >
                            {extractBlastErrorMessage(cancel.error)}
                        </P>
                    </div>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '0.8rem',
                        marginTop: '1.6rem'
                    }}
                >
                    <MarkButton type="button" onClick={onClose} disabled={cancel.isPending}>
                        Keep
                    </MarkButton>
                    <MarkButton
                        type="button"
                        onClick={handleConfirm}
                        disabled={cancel.isPending || (isSending && statsLoading)}
                    >
                        {cancel.isPending ? 'Cancelling…' : 'Cancel blast'}
                    </MarkButton>
                </div>
            </Section>
        </MarkModal>
    );
};
export default CancelModal;
